import {
  computed,
  inject,
  provide,
  ref,
  watch,
} from 'vue'

const DIALOG_STACK_SYMBOL = Symbol('Dialog stack')

/**
 * Keeps track of the opened dialogs, so that each dialog know if it's the last one or not
 *
 * One usecase is detecting wether the "escape" hotkey should be active on the dialog, as
 * it should be active only on the latest dialog opened when multiple ones are stacked.
 *
 * @param {*} state State of the dialog
 * @param {Object} options
 * @param {String} [options.name] Name of the dialog to display in the stack, for debugging purposes
 * @param {Boolean} [options.create] Wether to create a new stack for child components or not
 * @param {Boolean} [options.stackSymbol] Custom symbol to create multiple stacks in parallel
 * @returns Wether the dialog is on top of the stack or not
 */
function useDialogStack(state, {
  name = 'unnamed',
  create = false,
  stackSymbol = DIALOG_STACK_SYMBOL,
} = {}) {
  // Use the provided stack if any
  let stack = inject(stackSymbol, undefined)

  // If no stack injected by parent components, or a new one was requested, create it
  if (!stack || create) {
    stack = ref([])
  }

  // Create a symbol for the current dialog
  const dialogSymbol = Symbol(`Dialog: ${name}`)

  /*
  Watch when the state of the dialog changes
  When the dialog gets displayed, it's pushed on top of the stack
  When it gets hidden, it's removed from the stack
  */
  watch(state, (newState) => {
    if (newState) {
      stack.value.push(dialogSymbol)
    } else {
      stack.value = stack.value.filter((item) => item !== dialogSymbol)
    }
  }, { immediate: true })

  /*
  Compute wether the dialog is on top of the stack or not.
  If it is, it's the last element pushed.
  */
  const isOnTop = computed(() => {
    const stackItems = stack.value
    return stackItems[stackItems.length - 1] === dialogSymbol
  })

  // (re)provide the stack for children components
  provide(stackSymbol, stack)

  return isOnTop
}

export default useDialogStack
